import { DISMISS, useNotification } from "collections";
import { DeleteModal, useModalDispatch } from "collections/Modal";
import {
  getColumns,
  getSelectedWithoutHeader,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import {
  DevicesLocationQuery,
  Filter,
  GroupDeviceFragment,
  useDevicesLocationQuery,
  useRebootDevicesByGroupMutation
} from "generated/graphql";
import { infiniteNestLink, Link, Switch, useHistory } from "polyrouter";
import React, { useEffect, ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { State, useTypesafeActions } from "store";
import { TABLES, TABLE_COLUMNS, TABLE_COLUMN_STATE } from "store/userMeta";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { generateTableHeaders } from "utilities/tableUtils";
import { Layout } from "views/Main/Layouts";
import { needs, useGuard } from "rbac";
import {
  Guard,
  ProtectedButton,
  ProtectedLink,
  ProtectedRoute
} from "TsRoleGuards";
import VisibilitySensor from "react-visibility-sensor";
import { Metric } from "./DeviceGroupDetailsRouter";
import {
  areFiltersSelected,
  CategoryDiv,
  CategorySwitcher,
  ConnectionStatusIcon,
  DeviceFilterState,
  Loader,
  LoadingIndicator,
  NoResultsIndicatorAnimation,
  PremiumBetaBadge,
  ToolTip,
  useDeviceFilterValue
} from "elements";
import {
  constructFilterList,
  constructSortList,
  mapFieldToGraphQL,
  verifyAndFormatAggregates
} from "utilities/filters/deviceFilters";
import {
  ConnectionStatusCell,
  ConnectionStatusHeader
} from "routes/Manage/Inventory/List/TableView";
import { isUsbDevice } from "utilities/modelSelectors";
import { getStatusString } from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import { RestartDevicesModal } from "routes/Manage/Sites/View/DevicesTable";
import {
  useAddDevicesToGroup,
  useDeviceGroup,
  useRemoveDevicesFromGroup
} from "../DeviceGroupsUtils";
import { ActionType, createStandardAction, getType } from "typesafe-actions";
import { useSearchTerm } from "utilities/search";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

enum Tab {
  GROUP_DEVICES,
  AVAILABLE_TO_ADD
}

type GroupDevice = GroupDeviceFragment & {
  serialNumber: string;
};

type DeviceGroupOverviewState = {
  tab: Tab;
  selectedItems: Array<string>;
  searchTerm?: string;
  cursor: number;
  pageLoading: boolean;
  rebooted: boolean;
  actionInProgress: boolean;
};

const Actions = {
  updateTab: createStandardAction("UpdateTab")<
    DeviceGroupOverviewState["tab"]
  >(),
  updateSelectedItems: createStandardAction("UpdateSelectedItems")<
    DeviceGroupOverviewState["selectedItems"]
  >(),
  updateCursor: createStandardAction("UpdateCursor")<
    DeviceGroupOverviewState["cursor"]
  >(),
  updatePageLoading: createStandardAction("UpdatePageLoading")<
    DeviceGroupOverviewState["pageLoading"]
  >(),
  updatePageLoadingAndCursor: createStandardAction(
    "UpdatePageLoadingAndCursor"
  )<Pick<DeviceGroupOverviewState, "pageLoading" | "cursor">>(),
  updateRebooted: createStandardAction("UpdateRebooted")<
    DeviceGroupOverviewState["rebooted"]
  >(),
  updateActionInProgress: createStandardAction("UpdateActionInProgress")<
    DeviceGroupOverviewState["actionInProgress"]
  >(),
  updateActionInProgressAndCursor: createStandardAction(
    "UpdateActionInProgressAndCursor"
  )<Pick<DeviceGroupOverviewState, "actionInProgress" | "cursor">>()
};

type ActionTypes = typeof Actions;

function reducer(
  state: DeviceGroupOverviewState,
  a: ActionType<ActionTypes>
): DeviceGroupOverviewState {
  switch (a.type) {
    case getType(Actions.updateTab):
      return {
        ...state,
        tab: a.payload,
        selectedItems: []
      };
    case getType(Actions.updateSelectedItems):
      return {
        ...state,
        selectedItems: a.payload
      };
    case getType(Actions.updateCursor):
      return {
        ...state,
        cursor: a.payload
      };
    case getType(Actions.updatePageLoading):
      return {
        ...state,
        pageLoading: a.payload
      };
    case getType(Actions.updatePageLoadingAndCursor):
      return {
        ...state,
        pageLoading: a.payload.pageLoading,
        cursor: a.payload.cursor
      };
    case getType(Actions.updateRebooted):
      return {
        ...state,
        rebooted: a.payload
      };
    case getType(Actions.updateActionInProgress):
      return {
        ...state,
        actionInProgress: a.payload
      };
    case getType(Actions.updateActionInProgressAndCursor):
      return {
        ...state,
        actionInProgress: a.payload.actionInProgress,
        cursor: a.payload.cursor
      };
  }
}

export const DeviceGroupTitle: React.FC<{
  groupName: string;
  page: LocalKeys;
}> = ({ groupName, page }) => {
  const { t } = useTranslation();

  const { Content } = Layout;
  return (
    <Content.Title>
      {`${t(page)} `}
      <p>{`| ${groupName}`}</p>
    </Content.Title>
  );
};

export const DeviceGroupOverview: React.FC<{
  id: string;
  updateMetric: (metric: Metric) => void;
}> = ({ id, updateMetric }) => {
  const history = useHistory();
  const tenantId = useTenantId();
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const dismissModal = useModalDispatch();
  const removeDevicesFromGroup = useRemoveDevicesFromGroup();
  const addDevicesToGroup = useAddDevicesToGroup();
  const [, restart] = useRebootDevicesByGroupMutation();
  const [searchTerm, , clearSearch] = useSearchTerm();
  const [filters, setFilters, clearFilters] = useDeviceFilterValue();
  const [dispTid, setDispTid] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!dispTid) {
      setDispTid(tenantId);
      return;
    }
    if (tenantId !== dispTid) {
      history.push("/manage/device-groups");
    }
  }, [tenantId, dispTid, setDispTid, history]);

  const initialState: DeviceGroupOverviewState = {
    cursor: 0,
    pageLoading: false,
    selectedItems: [],
    tab: Tab.GROUP_DEVICES,
    rebooted: false,
    actionInProgress: false
  };
  const [
    { tab, selectedItems, cursor, pageLoading, rebooted, actionInProgress },
    {
      updateTab,
      updateSelectedItems,
      updateCursor,
      updatePageLoading,
      updatePageLoadingAndCursor,
      updateRebooted,
      updateActionInProgress,
      updateActionInProgressAndCursor
    }
  ] = useTypesafeActions(reducer, initialState, Actions);

  const COLUMNS = TABLE_COLUMNS.DEVICES;

  const constructFilterOptions = (
    devices: GroupDevice[],
    aggregates?: DevicesLocationQuery["deviceAggregations"]
  ) => {
    let models = new Set<string>();
    let sites = new Set<string>();
    let versions = new Set<string>();

    if (aggregates) {
      const verifyAndFormat = verifyAndFormatAggregates(aggregates);
      const getValue = (bucket: { value: string; count: number }) =>
        bucket.value;

      models = new Set(verifyAndFormat(COLUMNS.MODEL).map(getValue));
      sites = new Set(verifyAndFormat(COLUMNS.SITE).map(getValue));
      versions = new Set(verifyAndFormat(COLUMNS.VERSION).map(getValue));
    } else {
      devices.forEach(({ hardwareModel, site, softwareVersion }) => {
        if (hardwareModel) {
          models.add(hardwareModel);
        }
        if (site?.name) {
          sites.add(site?.name);
        }
        if (softwareVersion) {
          versions.add(softwareVersion);
        }
      });
    }

    return {
      [COLUMNS.MODEL]: Array.from(models),
      [COLUMNS.SITE]: Array.from(sites),
      [COLUMNS.VERSION]: Array.from(versions)
    };
  };

  const {
    data: group,
    fetching: groupFetching,
    stale: groupStale
  } = useDeviceGroup(id);

  const name = group?.name ?? "";
  const groupDevices = group?.devices ?? [];

  // only provisioned devices(have lastConfigRequestDate) with a serial number can be grouped
  // also, exclude devices already in group
  let availableDevicesFilters: Array<Filter> = [
    {
      field: "serialNumber",
      exists: true
    },
    {
      field: "serialNumber",
      ge: "0"
      // there are some devices with serialNumber="",
      // doesn't seem to be better way to exclude them using query than this
    },
    {
      field: "lastConfigRequestDate",
      exists: true
    },
    {
      field: "lastConfigRequestDate",
      gt: "0"
      // it seems there are some devices with non-null invalid lastConfigRequestDate,
      // this is the way to exclude them
    },
    ...groupDevices.map(device => ({
      field: "serialNumber",
      ne: device.serialNumber
    }))
  ];
  // apply additional filters for available devices only if user is on that tab
  const searchParams: Filter[] = searchTerm
    ? searchTerm.split(" ").map(word => ({ searchTerm: word }))
    : [{ searchTerm: "" }];
  if (tab === Tab.AVAILABLE_TO_ADD) {
    availableDevicesFilters = availableDevicesFilters.concat([
      ...constructFilterList(filters),
      searchTerm
        ? {
            AND: searchParams
          }
        : {}
    ]);
  }

  // fetch available devices
  const {
    data: devicesData,
    fetching: devicesFetching,
    stale: devicesStale
  } = useDevicesLocationQuery({
    variables: {
      from: cursor,
      boundsFilter: {
        AND: [
          {
            field: "tenantId",
            eq: tenantId
          },
          ...availableDevicesFilters
        ]
      },
      sort: constructSortList(filters),
      aggregations: [
        {
          term: "hardwareModel"
        },
        {
          term: "softwareVersion"
        },
        {
          term: "site"
        }
      ]
    }
  });

  const fetching = groupFetching || devicesFetching || devicesStale;
  useEffect(() => {
    if (!fetching) {
      updatePageLoading(false);
    }
  }, [fetching, updatePageLoading]);

  const availableDevices = devicesData?.deviceList ?? [];
  const availableDevicesCount = devicesData?.deviceCount ?? 0;

  let devices: GroupDevice[];
  let filterOptions: Record<string, string[]>;
  let pagination: PaginationProps | undefined;
  if (tab === Tab.GROUP_DEVICES) {
    devices = [...groupDevices].filter(device => {
      const term = searchTerm?.toLocaleLowerCase();
      const applySearchTerm =
        !term ||
        device.name?.toLocaleLowerCase().includes(term) ||
        device.serialNumber?.toLocaleLowerCase().includes(term) ||
        device.site?.name.toLocaleLowerCase().includes(term) ||
        device.hardwareModel?.toLocaleLowerCase().includes(term) ||
        device.softwareVersion?.toLocaleLowerCase().includes(term);
      const applyFilters = Object.entries(filters)
        .filter(([field, value]) => field !== "sort" && !!value)
        .every(([field, value]) => {
          const property = mapFieldToGraphQL[field];
          return (
            (
              (property === COLUMNS.SITE
                ? device.site?.name
                : device[property as keyof GroupDeviceFragment]) ?? ""
            ).toString() === value
          );
        });

      return !!device.serialNumber && applySearchTerm && applyFilters;
    }) as GroupDevice[];

    if (filters.sort) {
      const { field, direction } = filters.sort;
      sortTableData(devices, { field: mapFieldToGraphQL[field], direction });
    }

    filterOptions = constructFilterOptions(devices);
  } else {
    devices = [...availableDevices] as GroupDevice[];

    filterOptions = constructFilterOptions(
      devices,
      devicesData?.deviceAggregations ?? []
    );

    pagination = {
      count: availableDevicesCount,
      listLength: devices.length,
      setCursor: cursor =>
        updatePageLoadingAndCursor({
          pageLoading: true,
          cursor
        })
    };
  }

  useEffect(() => {
    updateMetric({
      type: t("DEVICES"),
      count: groupDevices.length
    });
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMetric, groupDevices.length]);

  const selectedDevices = getSelectedWithoutHeader(selectedItems);
  const anySelected = selectedDevices.length > 0;

  const removeDevices = async () => {
    if (!group) {
      return;
    }
    updateActionInProgress(true);
    const { data, error } = await removeDevicesFromGroup(
      group,
      selectedDevices
    );
    const response = data?.updateGroup;
    if (response?.success) {
      addNotification({
        type: "success",
        message: t("SUCCESSFULLY_DELETED_DEVICES_FROM_GROUP", {
          count: selectedDevices.length
        }),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
      updateSelectedItems([]);
      dismissModal();
    } else if (response?.error || error) {
      addNotification({
        type: "error",
        message: (response?.error || error?.message) ?? "err",
        dismiss: DISMISS.DEFAULT
      });
    }

    updateActionInProgressAndCursor({
      actionInProgress: false,
      cursor: 0
    });
  };

  const addDevices = async () => {
    if (!group) {
      return;
    }

    updateActionInProgress(true);
    const {
      result: { data, error }
    } = await addDevicesToGroup(
      group,
      availableDevices
        .filter(
          device =>
            !!device.serialNumber &&
            selectedDevices.includes(device.serialNumber)
        )
        .map(device => ({
          serial: device.serialNumber!,
          deviceModel: device.hardwareModel ?? ""
        }))
    );
    const response = data?.updateGroup;
    if (response?.success) {
      addNotification({
        type: "success",
        message: t("ADD_TO_EXISTING_GROUP_SUCCESS", {
          count: selectedDevices.length
        }),
        dismiss: DISMISS.DEFAULT
      });
      updateSelectedItems([]);
    } else if (response?.error || error) {
      addNotification({
        type: "error",
        message: (response?.error || error?.message) ?? "err",
        dismiss: DISMISS.DEFAULT
      });
    }

    updateActionInProgressAndCursor({
      actionInProgress: false,
      cursor: 0
    });
  };

  const switchTab = (tab: Tab) => {
    updateTab(tab);
    clearSearch(true);
    clearFilters(true);
  };

  const rebootDevices = async () => {
    const { data } = await restart({ groupId: id });
    return data?.rebootGroup;
  };
  let availableToAddCount,
    inThisGroupCount = "?";
  if (!fetching || pageLoading) {
    availableToAddCount = availableDevicesCount;
    inThisGroupCount =
      tab === Tab.GROUP_DEVICES
        ? String(devices.length)
        : String(groupDevices.length);
  }

  const { Content } = Layout;
  return (
    <Content className="device-group-details device-inventory">
      <DeviceGroupTitle groupName={name} page="OVERVIEW" />
      <section>
        <CategorySwitcher>
          <CategoryDiv
            label={t("IN_THIS_GROUP", { inThisGroupCount })}
            active={tab === Tab.GROUP_DEVICES}
            onClick={() => switchTab(Tab.GROUP_DEVICES)}
          />
          <CategoryDiv
            label={t("AVAILABLE_TO_ADD", { availableToAddCount })}
            active={tab === Tab.AVAILABLE_TO_ADD}
            onClick={() => switchTab(Tab.AVAILABLE_TO_ADD)}
          />
        </CategorySwitcher>
      </section>
      {tab === Tab.GROUP_DEVICES && groupDevices.length === 0 ? (
        <Loader loading={fetching || groupStale}>
          <NoResultsIndicatorAnimation className="inline pt-4">
            <span className="font-sm">{t("NO_DEVICES_IN_GROUP")}</span>
          </NoResultsIndicatorAnimation>
        </Loader>
      ) : (
        <>
          <GroupDevicesTable
            devices={devices}
            filters={filters}
            filterOptions={filterOptions}
            updateFilters={filters => {
              updateCursor(0);
              setFilters(filters, true);
            }}
            clearFilters={clearFilters}
            selected={selectedItems}
            updateSelected={updateSelectedItems}
            updateSearch={term => {
              if (term !== undefined && term !== searchTerm) {
                updateCursor(0);
              }
            }}
            pagination={pagination}
            actions={
              tab === Tab.GROUP_DEVICES ? (
                <span>
                  <ProtectedLink
                    requirement="UPDATE_SITES"
                    disabled={!anySelected}
                    to={anySelected ? infiniteNestLink("/remove") : ""}
                    className={`btn ${anySelected ? "" : "disabled"}`}
                  >
                    {t("REMOVE_FROM_GROUP")}
                  </ProtectedLink>
                  <ProtectedLink
                    to={!rebooted ? infiniteNestLink("/reboot") : ""}
                    className={`btn btn-secondary ${
                      rebooted ? "disabled" : ""
                    }`}
                    requirement="update:device"
                  >
                    {t("REBOOT_DEVICES")}
                  </ProtectedLink>
                  <Guard requirement="update:device">
                    <PremiumBetaBadge />
                  </Guard>
                </span>
              ) : (
                <ProtectedButton
                  disabled={!anySelected || actionInProgress}
                  className={`btn btn-primary ${anySelected ? "" : "disabled"}`}
                  onClick={addDevices}
                  requirement="UPDATE_SITES"
                >
                  {`${
                    actionInProgress
                      ? t("ADDING")
                      : `${t("ADD_TO_GROUP")}${
                          selectedDevices.length > 0
                            ? ` (${selectedDevices.length})`
                            : ""
                        }`
                  }`}
                </ProtectedButton>
              )
            }
            loading={fetching && !pageLoading}
          />
          {fetching && pageLoading ? (
            <LoadingIndicator className={devices.length ? "inline" : ""} />
          ) : null}
        </>
      )}
      <Switch>
        <ProtectedRoute nest path="/remove" requirement="UPDATE_SITES">
          <DeleteModal
            name="DeleteDevicesFromGroup"
            action="REMOVE"
            entity={t("DEVICES_FROM_GROUP")}
            confirmationString={t("CONFIRM_REMOVE_DEVICES_FROM_GROUP")}
            onDelete={removeDevices}
            disabled={actionInProgress}
          />
        </ProtectedRoute>
        <ProtectedRoute
          nest
          path="/reboot"
          requirement="update:device"
          render={() => (
            <RestartDevicesModal
              restart={rebootDevices}
              onSuccess={() => updateRebooted(true)}
            />
          )}
        />
      </Switch>
    </Content>
  );
};

type PaginationProps = {
  count: number;
  listLength: number;
  setCursor: (c: number) => void;
};

type GroupDevicesTableProps = {
  devices: Array<GroupDevice>;
  filters: DeviceFilterState;
  filterOptions: Record<string, Array<string>>;
  updateFilters: (filters: DeviceFilterState) => void;
  clearFilters: () => void;
  selected: string[];
  updateSelected: (items: string[]) => void;
  updateSearch: (term: string | undefined) => void;
  actions: ReactNode;
  pagination?: PaginationProps;
  loading: boolean;
};

const GroupDevicesTable: React.FC<GroupDevicesTableProps> = ({
  devices,
  filters,
  filterOptions,
  updateFilters,
  clearFilters,
  selected,
  updateSelected,
  updateSearch,
  actions,
  pagination,
  loading
}) => {
  const { t } = useTranslation();
  const COLUMNS = TABLE_COLUMNS.DEVICES;
  const { NAME: TABLE_NAME } = TABLES.DEVICE_GROUP_DEVICES;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const itemChecked = itemCheckedWithSet(selected);
  const toggleItem = toggleItemWithSet(
    devices.length,
    selected,
    updateSelected
  );

  const columnTranslations = {
    [COLUMNS.CONNECTION]: t("STATUS"),
    [COLUMNS.NAME]: t("DEVICE_NAME"),
    [COLUMNS.SERIAL_NUMBER]: t("SERIAL_NUMBER"),
    [COLUMNS.SITE]: t("SITE"),
    [COLUMNS.MODEL]: t("DEVICE_MODEL"),
    [COLUMNS.VERSION]: t("VERSION")
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);
  const userCanEdit = useGuard(needs("UPDATE_SITES"));
  // there might be one additional column besides those user choose to display (checkbox to select rows)
  const columnCount = shown.length + (userCanEdit ? 1 : 0);

  const getTableHeaders = (column: string, title: string) => {
    switch (column) {
      case COLUMNS.CONNECTION:
        return (
          <ConnectionStatusHeader
            fieldName="connected"
            filterState={filters}
            setFilter={updateFilters}
            key={column}
          />
        );
      default:
        const field = Object.keys(mapFieldToGraphQL).find(
          key => mapFieldToGraphQL[key] === column
        );
        const options = filterOptions[column];
        const filter =
          field && options
            ? {
                filterValue: (filters[field as keyof DeviceFilterState] ??
                  "") as string,
                filterOptions: options,
                onFilter: (_: string, value: string) => {
                  updateFilters({
                    ...filters,
                    [field]: value !== "" ? value : undefined
                  });
                }
              }
            : undefined;

        const { sort } = filters;
        return (
          <TableHeaderItem
            fieldName={column}
            title={title}
            sort={{
              currentSort: sort
                ? {
                    field: mapFieldToGraphQL[sort.field],
                    direction: sort.direction
                  }
                : {
                    field: COLUMNS.NAME,
                    direction: "ASC"
                  },
              onSort: sort =>
                updateFilters({
                  ...filters,
                  sort: {
                    field: field ?? sort.field,
                    direction: sort.direction
                  }
                })
            }}
            filter={filter}
            key={column}
          />
        );
    }
  };

  return (
    <>
      <div className="grouping pb-1 table-actions">
        {actions}
        <TableSearchEdit
          search={{
            onSearch: updateSearch,
            searchPlaceholder: t("SEARCH_DEVICES"),
            evaluateOnDemand: true
          }}
          edit={{
            tableName: TABLE_NAME,
            shownColumns: shown,
            hiddenColumns: hidden
          }}
        />
      </div>
      <div className="table-container">
        <table className="table custom-table table-filterable">
          <TableBulkItemHeader
            checked={itemChecked(HEADER_ID)}
            onCheck={() =>
              toggleAll(
                devices.map(device => device.serialNumber),
                selected,
                updateSelected
              )
            }
            showChecks={!!shownColumns.length}
            onClearFilters={clearFilters}
            showClearFilters={areFiltersSelected(filters)}
          >
            {generateTableHeaders(shown, getTableHeaders)}
          </TableBulkItemHeader>
          <TableBody loading={loading} cols={columnCount} accommodateScrollbar>
            {devices.map((device, index) => (
              <DeviceRow
                {...device}
                key={device.id}
                onCheck={() => toggleItem(device.serialNumber)}
                checked={itemChecked(device.serialNumber)}
                showChecks={!!shownColumns.length}
                shownColumns={shown.map(column => column.key)}
                pagination={pagination && { ...pagination, index }}
              />
            ))}
          </TableBody>
        </table>
      </div>
    </>
  );
};

type DeviceRowProps = GroupDevice & {
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  shownColumns: Array<string>;
  pagination?: PaginationProps & { index: number };
};

const DeviceRow: React.FC<DeviceRowProps> = ({
  id,
  name,
  serialNumber,
  hardwareModel,
  softwareVersion,
  site,
  connected,
  onCheck,
  checked,
  showChecks,
  shownColumns,
  pagination
}) => {
  const COLUMNS = TABLE_COLUMNS.DEVICES;
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);
  const onChange = (isVisible: boolean) => {
    if (isVisible) {
      setVisible(isVisible);
      if (pagination) {
        const { index, listLength, count, setCursor } = pagination;
        if (index === listLength - 1 && listLength < count) {
          setCursor(listLength);
        }
      }
    }
  };

  const isUsbDeviceModel = isUsbDevice(hardwareModel || "");

  const statusString = getStatusString(!!connected);

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.CONNECTION:
        return (
          <ConnectionStatusCell key={column}>
            {connected !== null && !isUsbDeviceModel && (
              <ToolTip
                iconTitle={t("STATUS_INDICATORS")}
                position="bottom"
                size="sm"
                iconReplacement={
                  <ConnectionStatusIcon connected={connected} className="m-0" />
                }
              >
                <span>{statusString}</span>
              </ToolTip>
            )}
          </ConnectionStatusCell>
        );
      case COLUMNS.NAME:
        content = (
          <Link to={`/manage/inventory/details/${id}`}>
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.SERIAL_NUMBER:
        content = handleNullFields(serialNumber);
        break;
      case COLUMNS.SITE:
        content = handleNullFields(site?.name, true);
        break;
      case COLUMNS.MODEL:
        content = handleNullFields(hardwareModel, true);
        break;
      case COLUMNS.VERSION:
        content = handleNullFields(softwareVersion, true);
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <VisibilitySensor
      offset={{ bottom: -300 }}
      active={!visible}
      onChange={onChange}
    >
      <TableBulkItemRow
        checked={checked}
        showChecks={showChecks}
        onCheck={() => onCheck(id)}
      >
        {shownColumns.map(getTableContent)}
      </TableBulkItemRow>
    </VisibilitySensor>
  );
};
